/* eslint-disable @typescript-eslint/no-shadow */
import React, { useState, useEffect } from 'react';
import EmptyList from '../../../../../../shared/design-system/components/molecules/empty-list/empty-list';
import { accessibleOnClick } from '../../../../../../shared/utils/accessible-on-click';
import { Template } from '../types';
import { SaleshandyTemplateLibrary, TabsKeys } from '../utils';
import PerformanceRenderer from './cells/performance-renderer';
import { Images } from '../../../../../../shared/app-constants';
import { ProductTour } from '../../../../../../shared/enums/product-tour';
import { getAttachmentIcon } from '../../../../../templates/components/templates-table/get-attachment-icon';
import { truncate } from '../../../../../../shared/utils';

type IProps = {
  templates: Template[];
  isTemplateRequestPending: boolean;
  tabKey: string;
  saleshandyTemplatesLibrary?: SaleshandyTemplateLibrary[];
  selectTemplateContent: (id, tabKey) => void;
};

const TemplatesList: React.FC<IProps> = ({
  templates,
  isTemplateRequestPending,
  tabKey,
  selectTemplateContent,
  saleshandyTemplatesLibrary,
}) => {
  const [selectedTemplatedId, setSelectedTemplateId] = useState<number>();

  const data = templates.map((template) => ({
    id: template.id,
    activity: {
      sentCount: template.sentCount,
      openCount: template.openCount,
    },
    subject: template.subject,
    content: template.content,
    title: template.template.title,
    shared: template.template.shared,
    isOwn: template.isOwn,
    templateAttachments: template.template.templateAttachments,
    user: {
      user: {
        firstName: template.template.user.firstName,
        lastName: template.template.user.lastName,
      },
    },
  }));

  const handleSelectTemplate = (id) => {
    setSelectedTemplateId(id);
    selectTemplateContent(id, tabKey);
  };

  const selectSaleshandyTemplate = (id, category) => {
    setSelectedTemplateId(id);
    selectTemplateContent(id, category);
  };

  const renderSaleshandyTemplates = (templates, category) =>
    templates.map(({ id, subject, template: { title } }) => (
      <div
        id={id === 1 && ProductTour.FirstTemplate}
        key={id}
        className={`saleshandy-template ${
          id === selectedTemplatedId && 'blue-17'
        } mt-1`}
        {...accessibleOnClick(() => selectSaleshandyTemplate(id, category))}
      >
        <div className="d-flex flex-column pl-3 pr-3 pt-2 pb-2">
          <span className="semibold-1 popover-arrow-color-txt">{title}</span>
          <span className="regular-1 gray-txt-27 subject">
            Sub: {subject || '<Empty Subject>'}
          </span>
        </div>
      </div>
    ));

  useEffect(() => {
    if (tabKey === TabsKeys.SALESHANDY_LIBRARY) {
      selectSaleshandyTemplate(
        saleshandyTemplatesLibrary[0]?.templates[0]?.id,
        saleshandyTemplatesLibrary[0]?.category,
      );
    }
    if (data.length && tabKey !== TabsKeys.SALESHANDY_LIBRARY) {
      handleSelectTemplate(data[0]?.id);
    }
  }, [isTemplateRequestPending, saleshandyTemplatesLibrary]);

  if (
    (!templates.length && tabKey !== TabsKeys.SALESHANDY_LIBRARY) ||
    (tabKey === TabsKeys.SALESHANDY_LIBRARY &&
      !saleshandyTemplatesLibrary[0]?.templates.length)
  ) {
    return (
      <div className="empty-list-container">
        <EmptyList
          title="No Template Found"
          description=""
          isVertical={true}
          imgSrc={Images.NoResult}
        />
      </div>
    );
  }

  const truncatedTextContent = (truncateString: string) =>
    truncate(truncateString, 40);

  return (
    <>
      <div className="template-list pl-4 pr-4">
        {tabKey === TabsKeys.SALESHANDY_LIBRARY
          ? saleshandyTemplatesLibrary.map((item) => (
              <div key={item?.category}>
                <span className="regular-1 font-bold gray-txt-15">
                  {item?.category}
                </span>
                {renderSaleshandyTemplates(item?.templates, item?.category)}
              </div>
            ))
          : data.map(
              ({ id, title, subject, activity, templateAttachments }) => (
                <div
                  key={id}
                  className={`template ${
                    id === selectedTemplatedId && 'blue-17'
                  } mt-1`}
                  {...accessibleOnClick(() => handleSelectTemplate(id))}
                >
                  <div className="d-flex flex-column pl-3 pr-3 pt-2 pb-2">
                    <span className="semibold-1 popover-arrow-color-txt">
                      {truncatedTextContent(title)}
                    </span>
                    <span className="regular-1 gray-txt-27 subject">
                      Sub: {truncatedTextContent(subject)}{' '}
                      {getAttachmentIcon(templateAttachments, [])}
                    </span>
                    <PerformanceRenderer
                      sentCount={activity?.sentCount}
                      openCount={activity?.openCount}
                    />
                  </div>
                </div>
              ),
            )}
      </div>
    </>
  );
};

export default TemplatesList;
